/**
 * Topology debug visualizations — pure computation functions.
 *
 * All functions operate on typed arrays and are framework-agnostic.
 * The renderer calls these each frame (for pose-aware modes) or once
 * (for static modes) and writes the results into a vertex color buffer.
 */

import type * as THREE from "three/webgpu";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type TopologyVizMode =
  | "stretch"
  | "normals"
  | "normalDeviation"
  | "density";

// ---------------------------------------------------------------------------
// Adjacency
// ---------------------------------------------------------------------------

/**
 * Build per-vertex adjacency from an indexed triangle mesh.
 * Returns an array where adjacency[vertexIndex] is a Uint32Array of
 * unique neighbor vertex indices.
 */
export function buildAdjacency(
  index: ArrayLike<number>,
  vertCount: number,
): Uint32Array[] {
  const neighborSets: Set<number>[] = new Array(vertCount);
  for (let i = 0; i < vertCount; i++) neighborSets[i] = new Set();

  for (let i = 0; i < index.length; i += 3) {
    const a = index[i],
      b = index[i + 1],
      c = index[i + 2];
    neighborSets[a].add(b);
    neighborSets[a].add(c);
    neighborSets[b].add(a);
    neighborSets[b].add(c);
    neighborSets[c].add(a);
    neighborSets[c].add(b);
  }

  const result: Uint32Array[] = new Array(vertCount);
  for (let i = 0; i < vertCount; i++) {
    result[i] = new Uint32Array(neighborSets[i]);
  }
  return result;
}

// ---------------------------------------------------------------------------
// Rest-pose edge lengths (computed once, cached)
// ---------------------------------------------------------------------------

/**
 * Compute the average edge length at each vertex in rest pose.
 * Used as the baseline for stretch/compression comparison.
 */
export function computeRestEdgeLengths(
  positions: Float32Array,
  adjacency: Uint32Array[],
): Float32Array {
  const vertCount = adjacency.length;
  const avgLengths = new Float32Array(vertCount);

  for (let vi = 0; vi < vertCount; vi++) {
    const neighbors = adjacency[vi];
    if (neighbors.length === 0) continue;

    const px = positions[vi * 3],
      py = positions[vi * 3 + 1],
      pz = positions[vi * 3 + 2];
    let sum = 0;
    for (let ni = 0; ni < neighbors.length; ni++) {
      const nv = neighbors[ni];
      const dx = positions[nv * 3] - px;
      const dy = positions[nv * 3 + 1] - py;
      const dz = positions[nv * 3 + 2] - pz;
      sum += Math.sqrt(dx * dx + dy * dy + dz * dz);
    }
    avgLengths[vi] = sum / neighbors.length;
  }

  return avgLengths;
}

// ---------------------------------------------------------------------------
// CPU skinning
// ---------------------------------------------------------------------------

/**
 * Skin vertex positions on CPU using the same bone matrices THREE.js uses on GPU.
 *
 * Formula per vertex:
 *   skinned = bindMatrixInverse × (Σ boneMatrix[i] × weight[i]) × bindMatrix × restPos
 */
export function skinPositions(
  restPositions: Float32Array,
  skinIndex: ArrayLike<number>,
  skinWeight: ArrayLike<number>,
  siStride: number,
  swStride: number,
  boneMatrices: Float32Array,
  bindMatrix: THREE.Matrix4,
  bindMatrixInverse: THREE.Matrix4,
  out: Float32Array,
): void {
  const vertCount = restPositions.length / 3;
  const bm = bindMatrix.elements;
  const bi = bindMatrixInverse.elements;

  for (let vi = 0; vi < vertCount; vi++) {
    // Apply bindMatrix to rest position
    const rx = restPositions[vi * 3];
    const ry = restPositions[vi * 3 + 1];
    const rz = restPositions[vi * 3 + 2];

    const bx = bm[0] * rx + bm[4] * ry + bm[8] * rz + bm[12];
    const by = bm[1] * rx + bm[5] * ry + bm[9] * rz + bm[13];
    const bz = bm[2] * rx + bm[6] * ry + bm[10] * rz + bm[14];

    // Weighted sum of bone transforms
    let sx = 0,
      sy = 0,
      sz = 0;
    for (let slot = 0; slot < 4; slot++) {
      const w = skinWeight[vi * swStride + slot];
      if (w === 0) continue;

      const boneIdx = Math.round(skinIndex[vi * siStride + slot]);
      const off = boneIdx * 16;
      const m = boneMatrices;

      sx += w * (m[off] * bx + m[off + 4] * by + m[off + 8] * bz + m[off + 12]);
      sy +=
        w * (m[off + 1] * bx + m[off + 5] * by + m[off + 9] * bz + m[off + 13]);
      sz +=
        w *
        (m[off + 2] * bx + m[off + 6] * by + m[off + 10] * bz + m[off + 14]);
    }

    // Apply bindMatrixInverse
    out[vi * 3] = bi[0] * sx + bi[4] * sy + bi[8] * sz + bi[12];
    out[vi * 3 + 1] = bi[1] * sx + bi[5] * sy + bi[9] * sz + bi[13];
    out[vi * 3 + 2] = bi[2] * sx + bi[6] * sy + bi[10] * sz + bi[14];
  }
}

/**
 * Skin vertex normals on CPU (rotation only — no translation).
 *
 * For rigid bone rotations the normal matrix is just the upper-3x3
 * of the bone matrix (no need for inverse-transpose when bones are
 * orthonormal rotation + translation).
 */
export function skinNormals(
  restNormals: Float32Array,
  skinIndex: ArrayLike<number>,
  skinWeight: ArrayLike<number>,
  siStride: number,
  swStride: number,
  boneMatrices: Float32Array,
  bindMatrix: THREE.Matrix4,
  bindMatrixInverse: THREE.Matrix4,
  out: Float32Array,
): void {
  const vertCount = restNormals.length / 3;
  const bm = bindMatrix.elements;
  const bi = bindMatrixInverse.elements;

  for (let vi = 0; vi < vertCount; vi++) {
    const rx = restNormals[vi * 3];
    const ry = restNormals[vi * 3 + 1];
    const rz = restNormals[vi * 3 + 2];

    // Rotate by bindMatrix (3x3 only, no translation for normals)
    const bx = bm[0] * rx + bm[4] * ry + bm[8] * rz;
    const by = bm[1] * rx + bm[5] * ry + bm[9] * rz;
    const bz = bm[2] * rx + bm[6] * ry + bm[10] * rz;

    let sx = 0,
      sy = 0,
      sz = 0;
    for (let slot = 0; slot < 4; slot++) {
      const w = skinWeight[vi * swStride + slot];
      if (w === 0) continue;

      const boneIdx = Math.round(skinIndex[vi * siStride + slot]);
      const off = boneIdx * 16;
      const m = boneMatrices;

      // 3x3 rotation only
      sx += w * (m[off] * bx + m[off + 4] * by + m[off + 8] * bz);
      sy += w * (m[off + 1] * bx + m[off + 5] * by + m[off + 9] * bz);
      sz += w * (m[off + 2] * bx + m[off + 6] * by + m[off + 10] * bz);
    }

    // Rotate by bindMatrixInverse (3x3)
    const fx = bi[0] * sx + bi[4] * sy + bi[8] * sz;
    const fy = bi[1] * sx + bi[5] * sy + bi[9] * sz;
    const fz = bi[2] * sx + bi[6] * sy + bi[10] * sz;

    // Normalize
    const len = Math.sqrt(fx * fx + fy * fy + fz * fz) || 1;
    out[vi * 3] = fx / len;
    out[vi * 3 + 1] = fy / len;
    out[vi * 3 + 2] = fz / len;
  }
}

// ---------------------------------------------------------------------------
// Stretch / compression
// ---------------------------------------------------------------------------

/**
 * Compute per-vertex stretch ratio: current average edge length / rest average edge length.
 * Ratio > 1 = stretched, < 1 = compressed, ≈ 1 = unchanged.
 */
export function computeStretch(
  skinnedPositions: Float32Array,
  adjacency: Uint32Array[],
  restEdgeLengths: Float32Array,
): Float32Array {
  const vertCount = adjacency.length;
  const ratios = new Float32Array(vertCount);

  for (let vi = 0; vi < vertCount; vi++) {
    const neighbors = adjacency[vi];
    const restLen = restEdgeLengths[vi];
    if (neighbors.length === 0 || restLen < 1e-8) {
      ratios[vi] = 1;
      continue;
    }

    const px = skinnedPositions[vi * 3];
    const py = skinnedPositions[vi * 3 + 1];
    const pz = skinnedPositions[vi * 3 + 2];
    let sum = 0;
    for (let ni = 0; ni < neighbors.length; ni++) {
      const nv = neighbors[ni];
      const dx = skinnedPositions[nv * 3] - px;
      const dy = skinnedPositions[nv * 3 + 1] - py;
      const dz = skinnedPositions[nv * 3 + 2] - pz;
      sum += Math.sqrt(dx * dx + dy * dy + dz * dz);
    }

    ratios[vi] = sum / neighbors.length / restLen;
  }

  return ratios;
}

// ---------------------------------------------------------------------------
// Normal orientation (back-face detection)
// ---------------------------------------------------------------------------

/**
 * Compute per-vertex facing ratio: dot(skinnedNormal, vertexToCamera).
 * Positive = front-facing, negative = back-facing.
 * Output is in [-1, 1] range.
 */
export function computeNormalOrientation(
  skinnedPositions: Float32Array,
  skinnedNormals: Float32Array,
  cameraWorldPos: THREE.Vector3,
  objectWorldMatrix: THREE.Matrix4,
  out: Float32Array,
): void {
  const vertCount = skinnedPositions.length / 3;
  const ow = objectWorldMatrix.elements;

  for (let vi = 0; vi < vertCount; vi++) {
    // Transform position to world space
    const lx = skinnedPositions[vi * 3];
    const ly = skinnedPositions[vi * 3 + 1];
    const lz = skinnedPositions[vi * 3 + 2];
    const wx = ow[0] * lx + ow[4] * ly + ow[8] * lz + ow[12];
    const wy = ow[1] * lx + ow[5] * ly + ow[9] * lz + ow[13];
    const wz = ow[2] * lx + ow[6] * ly + ow[10] * lz + ow[14];

    // Vertex-to-camera direction
    let vx = cameraWorldPos.x - wx;
    let vy = cameraWorldPos.y - wy;
    let vz = cameraWorldPos.z - wz;
    const vLen = Math.sqrt(vx * vx + vy * vy + vz * vz) || 1;
    vx /= vLen;
    vy /= vLen;
    vz /= vLen;

    // Transform normal to world space (rotation only)
    const nx = skinnedNormals[vi * 3];
    const ny = skinnedNormals[vi * 3 + 1];
    const nz = skinnedNormals[vi * 3 + 2];
    const wnx = ow[0] * nx + ow[4] * ny + ow[8] * nz;
    const wny = ow[1] * nx + ow[5] * ny + ow[9] * nz;
    const wnz = ow[2] * nx + ow[6] * ny + ow[10] * nz;

    out[vi] = wnx * vx + wny * vy + wnz * vz;
  }
}

// ---------------------------------------------------------------------------
// Normal deviation
// ---------------------------------------------------------------------------

/**
 * Compute per-vertex angle (radians) between the stored smooth normal and the
 * geometric face normal (angle-weighted average of surrounding faces).
 *
 * This is pose-aware: uses skinned positions and skinned normals.
 */
export function computeNormalDeviation(
  skinnedPositions: Float32Array,
  skinnedNormals: Float32Array,
  index: ArrayLike<number>,
  out: Float32Array,
): void {
  const vertCount = skinnedPositions.length / 3;

  // Accumulate angle-weighted geometric normals per vertex
  const geoNx = new Float32Array(vertCount);
  const geoNy = new Float32Array(vertCount);
  const geoNz = new Float32Array(vertCount);

  for (let i = 0; i < index.length; i += 3) {
    const ia = index[i],
      ib = index[i + 1],
      ic = index[i + 2];

    // Edge vectors
    const ax = skinnedPositions[ia * 3],
      ay = skinnedPositions[ia * 3 + 1],
      az = skinnedPositions[ia * 3 + 2];
    const bx = skinnedPositions[ib * 3],
      by = skinnedPositions[ib * 3 + 1],
      bz = skinnedPositions[ib * 3 + 2];
    const cx = skinnedPositions[ic * 3],
      cy = skinnedPositions[ic * 3 + 1],
      cz = skinnedPositions[ic * 3 + 2];

    const abx = bx - ax,
      aby = by - ay,
      abz = bz - az;
    const acx = cx - ax,
      acy = cy - ay,
      acz = cz - az;
    const bcx = cx - bx,
      bcy = cy - by,
      bcz = cz - bz;

    // Face normal (unnormalized — magnitude encodes area)
    const fnx = aby * acz - abz * acy;
    const fny = abz * acx - abx * acz;
    const fnz = abx * acy - aby * acx;
    const fLen = Math.sqrt(fnx * fnx + fny * fny + fnz * fnz);
    if (fLen < 1e-10) continue; // degenerate

    const fnxn = fnx / fLen,
      fnyn = fny / fLen,
      fnzn = fnz / fLen;

    // Angle at each vertex of this face
    const abLen = Math.sqrt(abx * abx + aby * aby + abz * abz) || 1;
    const acLen = Math.sqrt(acx * acx + acy * acy + acz * acz) || 1;
    const bcLen = Math.sqrt(bcx * bcx + bcy * bcy + bcz * bcz) || 1;

    const angleA = Math.acos(
      Math.max(
        -1,
        Math.min(1, (abx * acx + aby * acy + abz * acz) / (abLen * acLen)),
      ),
    );

    const bax = -abx,
      bay = -aby,
      baz = -abz;
    const angleB = Math.acos(
      Math.max(
        -1,
        Math.min(1, (bax * bcx + bay * bcy + baz * bcz) / (abLen * bcLen)),
      ),
    );

    const angleC = Math.PI - angleA - angleB;

    // Accumulate weighted face normals
    geoNx[ia] += fnxn * angleA;
    geoNy[ia] += fnyn * angleA;
    geoNz[ia] += fnzn * angleA;
    geoNx[ib] += fnxn * angleB;
    geoNy[ib] += fnyn * angleB;
    geoNz[ib] += fnzn * angleB;
    geoNx[ic] += fnxn * angleC;
    geoNy[ic] += fnyn * angleC;
    geoNz[ic] += fnzn * angleC;
  }

  // Compare geometric normal to stored smooth normal
  for (let vi = 0; vi < vertCount; vi++) {
    const gnx = geoNx[vi],
      gny = geoNy[vi],
      gnz = geoNz[vi];
    const gLen = Math.sqrt(gnx * gnx + gny * gny + gnz * gnz);
    if (gLen < 1e-10) {
      out[vi] = 0;
      continue;
    }

    const nx = skinnedNormals[vi * 3],
      ny = skinnedNormals[vi * 3 + 1],
      nz = skinnedNormals[vi * 3 + 2];
    const dot = (gnx * nx + gny * ny + gnz * nz) / gLen;
    out[vi] = Math.acos(Math.max(-1, Math.min(1, dot)));
  }
}

// ---------------------------------------------------------------------------
// Vertex density (static — rest-pose only)
// ---------------------------------------------------------------------------

/**
 * Compute per-vertex relative density: average edge length vs mesh-wide average.
 * Values < 1 = denser than average, > 1 = sparser than average.
 */
export function computeDensity(
  positions: Float32Array,
  adjacency: Uint32Array[],
): Float32Array {
  const vertCount = adjacency.length;
  const avgLengths = new Float32Array(vertCount);
  let globalSum = 0;
  let globalCount = 0;

  for (let vi = 0; vi < vertCount; vi++) {
    const neighbors = adjacency[vi];
    if (neighbors.length === 0) continue;

    const px = positions[vi * 3],
      py = positions[vi * 3 + 1],
      pz = positions[vi * 3 + 2];
    let sum = 0;
    for (let ni = 0; ni < neighbors.length; ni++) {
      const nv = neighbors[ni];
      const dx = positions[nv * 3] - px;
      const dy = positions[nv * 3 + 1] - py;
      const dz = positions[nv * 3 + 2] - pz;
      sum += Math.sqrt(dx * dx + dy * dy + dz * dz);
    }
    const avg = sum / neighbors.length;
    avgLengths[vi] = avg;
    globalSum += avg;
    globalCount++;
  }

  const globalAvg = globalCount > 0 ? globalSum / globalCount : 1;
  const density = new Float32Array(vertCount);
  for (let vi = 0; vi < vertCount; vi++) {
    density[vi] = avgLengths[vi] / globalAvg;
  }
  return density;
}

// ---------------------------------------------------------------------------
// Color ramps
// ---------------------------------------------------------------------------

/**
 * Stretch/compression diverging ramp:
 *   blue (compressed < 0.8) → dark (neutral ≈ 1.0) → red (stretched > 1.2)
 *   Dead zone: 0.95–1.05 maps to near-black.
 */
export function stretchToColor(
  ratio: number,
  out: Float32Array,
  offset: number,
): void {
  const DEAD_LO = 0.95,
    DEAD_HI = 1.05;
  const COMPRESS_MAX = 0.7; // ratio at which blue saturates
  const STRETCH_MAX = 1.4; // ratio at which red saturates

  if (ratio >= DEAD_LO && ratio <= DEAD_HI) {
    // Dead zone — near-black
    out[offset] = 0.04;
    out[offset + 1] = 0.04;
    out[offset + 2] = 0.06;
    return;
  }

  if (ratio < DEAD_LO) {
    // Compression: dark → blue
    const t = Math.min(1, (DEAD_LO - ratio) / (DEAD_LO - COMPRESS_MAX));
    out[offset] = 0.04 * (1 - t);
    out[offset + 1] = 0.04 * (1 - t) + 0.3 * t;
    out[offset + 2] = 0.06 + 0.94 * t;
  } else {
    // Stretch: dark → yellow → red
    const t = Math.min(1, (ratio - DEAD_HI) / (STRETCH_MAX - DEAD_HI));
    if (t < 0.5) {
      const s = t * 2;
      out[offset] = 0.04 + 0.96 * s;
      out[offset + 1] = 0.04 + 0.76 * s;
      out[offset + 2] = 0.06 * (1 - s);
    } else {
      const s = (t - 0.5) * 2;
      out[offset] = 1;
      out[offset + 1] = 0.8 * (1 - s);
      out[offset + 2] = 0;
    }
  }
}

/**
 * Normal orientation ramp:
 *   dark green (front-facing, dot > 0.2) → yellow (grazing, dot ≈ 0) → red (back-facing, dot < -0.2)
 */
export function orientationToColor(
  dot: number,
  out: Float32Array,
  offset: number,
): void {
  if (dot > 0.2) {
    // Front-facing: dark green
    const t = Math.min(1, (dot - 0.2) / 0.8);
    out[offset] = 0;
    out[offset + 1] = 0.15 + 0.25 * t;
    out[offset + 2] = 0.05;
  } else if (dot > -0.2) {
    // Grazing: yellow
    const t = (dot + 0.2) / 0.4; // 0 at back edge, 1 at front edge
    out[offset] = 1;
    out[offset + 1] = 0.5 + 0.5 * t;
    out[offset + 2] = 0;
  } else {
    // Back-facing: red
    const t = Math.min(1, (-0.2 - dot) / 0.8);
    out[offset] = 0.8 + 0.2 * t;
    out[offset + 1] = 0.1 * (1 - t);
    out[offset + 2] = 0;
  }
}

/**
 * Normal deviation ramp:
 *   dark (< 5°) → yellow (~30°) → red (> 60°)
 */
export function deviationToColor(
  angleRad: number,
  out: Float32Array,
  offset: number,
): void {
  const deg = angleRad * (180 / Math.PI);
  const LOW = 5,
    MID = 30,
    HIGH = 60;

  if (deg < LOW) {
    out[offset] = 0.04;
    out[offset + 1] = 0.06;
    out[offset + 2] = 0.04;
  } else if (deg < MID) {
    const t = (deg - LOW) / (MID - LOW);
    out[offset] = t;
    out[offset + 1] = 0.7 * t + 0.06 * (1 - t);
    out[offset + 2] = 0.04 * (1 - t);
  } else {
    const t = Math.min(1, (deg - MID) / (HIGH - MID));
    out[offset] = 1;
    out[offset + 1] = 0.7 * (1 - t);
    out[offset + 2] = 0;
  }
}

/**
 * Density diverging ramp:
 *   red (dense, ratio < 0.5) → dark (average, ratio ≈ 1.0) → blue (sparse, ratio > 2.0)
 *
 * Note: ratio is avg-edge-length / global-avg, so SMALLER ratio = DENSER.
 */
export function densityToColor(
  ratio: number,
  out: Float32Array,
  offset: number,
): void {
  const DENSE = 0.5,
    SPARSE = 2.0;
  const DEAD_LO = 0.8,
    DEAD_HI = 1.2;

  if (ratio >= DEAD_LO && ratio <= DEAD_HI) {
    out[offset] = 0.04;
    out[offset + 1] = 0.04;
    out[offset + 2] = 0.06;
    return;
  }

  if (ratio < DEAD_LO) {
    // Dense: dark → red
    const t = Math.min(1, (DEAD_LO - ratio) / (DEAD_LO - DENSE));
    out[offset] = 0.04 + 0.96 * t;
    out[offset + 1] = 0.04 * (1 - t);
    out[offset + 2] = 0.06 * (1 - t);
  } else {
    // Sparse: dark → blue
    const t = Math.min(1, (ratio - DEAD_HI) / (SPARSE - DEAD_HI));
    out[offset] = 0.04 * (1 - t);
    out[offset + 1] = 0.04 * (1 - t) + 0.3 * t;
    out[offset + 2] = 0.06 + 0.94 * t;
  }
}
